import React from "react";
import { usd } from "@/lib/format";
import { Check, Layers } from "lucide-react";
import { ROOF_TYPE_TONE, tLabel, systemName } from "@/lib/roofTypes";

const assetSrc = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

function Photos({ title, photos, tone, testid }) {
  if (!photos?.length) return null;
  return (
    <div data-testid={testid}>
      <div className={`text-xs font-semibold uppercase mb-1.5 ${tone}`}>{title}</div>
      <div className="grid grid-cols-2 gap-2">
        {photos.map((ph, i) => (
          <div key={i}><img src={assetSrc(ph.url)} alt="" loading="lazy" className="rounded-xl w-full h-36 object-cover" />{ph.caption && <p className="text-xs text-slate-500 mt-1">{ph.caption}</p>}</div>
        ))}
      </div>
    </div>
  );
}

function SystemSection({ s, i, multi }) {
  const name = systemName(s, multi && !s.label ? i : null);
  return (
    <div className="mb-card p-5 space-y-4" data-testid={`prop-system-${i}`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
        <div>
          <span className={`inline-block ${ROOF_TYPE_TONE[s.roof_type]} text-white text-[11px] font-bold uppercase px-2.5 py-0.5 rounded-full mb-2`}>{multi ? `System ${i + 1} · ` : ""}{tLabel(s.roof_type)}</span>
          <h3 className="font-display text-2xl font-bold uppercase text-navy leading-tight">{name}</h3>
          <div className="text-sm text-slate-500">{[s.product, s.color, `${s.squares || 0} squares`].filter(Boolean).join(" · ")}</div>
        </div>
        <div className="font-num text-3xl font-extrabold text-navy" data-testid={`prop-system-price-${i}`}>{usd(s.price_cents)}</div>
      </div>
      {(s.before_photos?.length > 0 || s.after_photos?.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Photos title="Before" photos={s.before_photos} tone="text-slate-400" testid={`prop-system-before-${i}`} />
          <Photos title="After" photos={s.after_photos} tone="text-brandred" testid={`prop-system-after-${i}`} />
        </div>
      )}
      {s.scope_items?.length > 0 && (
        <div data-testid={`prop-system-scope-${i}`}>
          <div className="font-display font-bold uppercase text-navy mb-1 flex items-center gap-2"><Layers className="w-4 h-4" /> Scope of Work</div>
          {s.scope_items.map((it, k) => (
            <div key={k} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
              {it.image_url ? <img src={assetSrc(it.image_url)} alt={it.title} loading="lazy" className="w-14 h-14 rounded-lg object-cover shrink-0 border border-slate-200" /> : <div className="w-14 h-14 rounded-lg bg-slate-100 shrink-0 border border-slate-200" />}
              <div className="flex-1"><div className="text-sm font-semibold text-navy flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-successg shrink-0" /> {it.title}</div><div className="text-xs text-slate-500 mt-0.5">{it.description}</div></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ProposalSystemsView({ p }) {
  const multi = p.systems.length > 1;
  return (
    <div className="space-y-4" data-testid="prop-systems">
      <h2 className="font-display text-2xl font-bold uppercase text-navy">{multi ? "Your Roof Systems" : "Your Roof System"}</h2>
      {p.systems.map((s, i) => <SystemSection key={s.id || i} s={s} i={i} multi={multi} />)}
    </div>
  );
}

export function InvestmentSummary({ p }) {
  const multi = p.systems.length > 1;
  return (
    <div className="mb-card p-5" data-testid="prop-investment">
      <h2 className="font-display text-2xl font-bold uppercase text-navy mb-3">Investment Summary</h2>
      {p.systems.map((s, i) => (
        <div key={s.id || i} className="flex justify-between text-sm py-2 border-b border-slate-100">
          <span className="text-navy font-semibold">{systemName(s, multi && !s.label ? i : null)} <span className="text-slate-400 font-normal">· {tLabel(s.roof_type)} · {s.squares || 0} sq</span></span>
          <span className="font-num font-bold text-navy">{usd(s.price_cents)}</span>
        </div>
      ))}
      {(p.upgrades || []).filter((u) => (p.selected_upgrade_ids || []).includes(u.id)).map((u) => (
        <div key={u.id} className="flex justify-between text-sm py-2 border-b border-slate-100" data-testid={`prop-investment-upgrade-${u.id}`}>
          <span className="text-navy font-semibold">{u.title} <span className="text-slate-400 font-normal">· upgrade</span></span>
          <span className="font-num font-bold text-navy">{usd(u.price_cents)}</span>
        </div>
      ))}
      <div className="flex justify-between items-center mt-3 bg-navy text-white rounded-xl px-4 py-3">
        <span className="font-display font-bold uppercase">Total Investment</span>
        <span className="font-num text-2xl font-extrabold" data-testid="prop-total-investment">{usd(p.amount_cents)}</span>
      </div>
      {p.upgrades?.length > 0 && <p className="text-xs text-slate-400 mt-2">Tick any optional upgrade above to add it to this total.</p>}
    </div>
  );
}
